import {
  Suspense,
  lazy,
  useState,
  useEffect,
  useRef,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import {
  useTrackToggle,
  RoomContext,
  StartAudio,
} from '@livekit/components-react';
import type { Room } from 'livekit-client';
import { Track } from 'livekit-client';
import {
  AudioLines,
  ChevronDown,
  ChevronUp,
  Keyboard,
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  Pause,
  PhoneOff,
  Send,
  Square,
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import type { VoiceState, VoicePauseReason } from '@/hooks/v2/useVoiceConversation';
import type { VoiceEndReason } from '@shared/livekit-constants';
import type { InputMode } from '@/hooks/v2/useConversation';
import { type MicErrorKind, micErrorHint } from '@/hooks/v2/micError';
import { useIsMobile } from '@/hooks/use-mobile';
import { VoiceSessionTimer } from './VoiceSessionTimer';
import { AnakaBarVisualizerView } from './AnakaBarVisualizerView';
import { mapVoiceVisualState } from './voiceVisualState';
import {
  micButtonPresentation,
  micLockAnnouncement,
  playMicLockEarcon,
} from './micLock';

const AnakaBarVisualizer = lazy(() =>
  import('./AnakaBarVisualizer').then((mod) => ({ default: mod.AnakaBarVisualizer })),
);

export interface ConversationInputBarProps {
  /**
   * Server/session gate: `true` once `conversation_id` exists and transport is
   * not in error (same semantics as `useConversation().inputReady`).
   */
  inputReady: boolean;
  /**
   * When `true` (default), text field + send wait for `inputReady`. Home lobby
   * passes `false` so the first user message can lazy-start without /start.
   */
  textRequiresSessionReady?: boolean;
  onSend: (text: string) => Promise<void>;
  /**
   * True while a turn is in flight (thinking or streaming). Disables Send + the
   * Enter shortcut so the same turn can't be fired repeatedly mid-flight. Obs 2:
   * a slow framing turn left Send enabled and the input un-cleared, so the user
   * resent the identical message (3× in one second in the Cadence HAR); each
   * resend aborts and restarts the turn, compounding the stall. Optional —
   * defaults off, so un-updated call sites are unaffected.
   */
  busy?: boolean;
  placeholder?: string;
  inputMode: InputMode;
  /** Mic in text zone: switches to voice + save pref — caller invokes `connectVoice()`. */
  onSwitchToVoice: () => void;
  /** Exit voice pref / idle / active → text + disconnect; save pref to profile. */
  onSwitchToText: () => void;
  /** VoiceIdleZone "Start voice" — `connectVoice()` only (preference is already voice). */
  onStartVoiceFromIdle: () => Promise<void>;
  /**
   * 004-p7 A3.2 (ANA-658) — disable the voice-START affordances (the text-zone Mic
   * and the VoiceIdleZone "Start") until the session id is committed. On a surface that
   * auto-starts text (scoping/proactiveStart), starting voice BEFORE `/start` resolves
   * keys the LiveKit room on a fresh fallback UUID that then diverges from the server's
   * resolved id (voice↔text split-brain). Gating the control — rather than silently
   * deferring the connect — is the honest UX. Default `false` (home lobby is lazy: the
   * shared client id is authoritative, so voice is always ready there). Wire from
   * `!useConversation().voiceStartReady`.
   */
  voiceStartDisabled?: boolean;
  voiceState: VoiceState;
  /** Custom mid-turn voice status label from n8n (spec 015 US4); overrides "Thinking". */
  statusLabelOverride?: string | null;
  /** ANA-407 — true while the two-phase idle "still there?" prompt is showing; overrides the "Listening" label. */
  idlePrompting?: boolean;
  /** ANA-409 — true within the pre-cap warning window of the hard cost-cap auto-pause; shows the wind-down banner. */
  sessionCapWarning?: boolean;
  /** ANA-409 — "Continue" tapped on the wind-down banner: reset the cost-cap window. */
  onExtendSession?: () => void;
  /** ANA-409 — "Lock the scope" tapped on the wind-down banner. */
  onLockScope?: () => void;
  /**
   * Text-mode mid-turn status (`send_status_update`) — shown inside the input
   * box in place of the message field while a text turn is in flight (parity
   * with the voice bar's status label). Null until the first status arrives.
   */
  textStatusUpdate?: { text: string; label: string } | null;
  room: Room | null;
  livekitUrl: string | null;
  /** True when the mic could not be published on connect (denied prompt). Surfaced as a soft hint. */
  micBlocked?: boolean;
  /** Why the mic publish failed — drives honest copy (no-device vs denied vs in-use). */
  micErrorKind?: MicErrorKind | null;
  /** ANA-738 (B1) — true while the mic is auto-locked during `thinking`. */
  micLocked?: boolean;
  paused: boolean;
  /** Why the call is paused — an idle (no-mic-input) pause shows a specific status. */
  pauseReason?: VoicePauseReason | null;
  voiceError: Error | null;
  /** ANA-575 — why the agent ended the call, when `voiceState === 'ended'`. */
  voiceEndedReason?: VoiceEndReason | null;
  voiceStartedAt: number | null;
  pausedMs: number;
  onPause: () => void;
  onResume: () => Promise<void>;
  onEndCall: () => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
  /**
   * Shell mode the bar is rendered in. When `'detailed'` the in-bar
   * Maximize2 toggle is shown; when `'focused'` the Minimize2 toggle is
   * shown; when `undefined` (or `'peek'`, which has no visible bar) the
   * toggle is hidden.
   */
  mode?: 'peek' | 'detailed' | 'focused';
  /**
   * Flip Detailed ↔ Focused. The bar simply calls this on click — the
   * shell owns the actual mode state. Hidden if absent.
   */
  onToggleMode?: () => void;
  /**
   * 017 — when false, the ACTIVE voice chrome renders in-flow (below the thread)
   * instead of the default viewport-fixed overlay. Used by the scoping page so
   * the thread + voice bar share the page's main column.
   */
  pinnedVoiceChrome?: boolean;
}

// ─── Handoff glass chrome ────────────────────────────────────────────────────

function chromeStyle(shadow = true): CSSProperties {
  return {
    borderRadius: 14,
    background: 'var(--chat-surface)',
    border: '1px solid var(--chat-border)',
    backdropFilter: 'blur(20px)',
    boxShadow: shadow ? 'var(--chat-shadow)' : 'none',
  };
}

/**
 * Fixed-bottom wrapper preserves the existing FIXED_BAR_CLASS positioning
 * semantics used by VoiceLiveChrome while applying the handoff glass surface.
 */
const FIXED_POSITION_STYLE: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 50,
  // Clear the home indicator on safe-area (notched) devices.
  paddingBottom: 'env(safe-area-inset-bottom)',
};

// ─── Primitive: ChatIconBtn ──────────────────────────────────────────────────
// Handoff spec: 32×32, radius 8.
// primary  = gradient bg + white icon + shadow
// non-primary = var(--secondary) bg + 1px var(--border) + muted-foreground icon

interface ChatIconBtnProps {
  children: React.ReactNode;
  primary?: boolean;
  /** ANA-738 — amber "mic auto-locked" affordance, distinct from muted. */
  locked?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  'data-testid'?: string;
  'data-mode'?: string;
  type?: 'button' | 'submit' | 'reset';
}

function ChatIconBtn({
  children,
  primary,
  locked,
  onClick,
  disabled,
  title,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  'data-testid': dataTestId,
  'data-mode': dataMode,
  type = 'button',
}: ChatIconBtnProps) {
  const base: CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 8,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition: 'opacity 0.15s',
  };

  const themed: CSSProperties = primary
    ? {
        background: 'linear-gradient(135deg, #7C3AED, #3B82F6)',
        border: 'none',
        boxShadow: '0 4px 14px rgba(124,58,237,0.30)',
        color: 'white',
      }
    : locked
      ? {
          // ANA-738 — amber "working / mic paused" affordance (wait colour family).
          background: 'rgba(245,158,11,0.12)',
          border: '1px solid rgba(245,158,11,0.40)',
          boxShadow: 'none',
          color: '#F59E0B',
        }
      : {
          background: 'var(--secondary)',
          border: '1px solid var(--border)',
          boxShadow: 'none',
          color: 'var(--muted-foreground)',
        };

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? title}
      aria-pressed={ariaPressed}
      data-testid={dataTestId}
      data-mode={dataMode}
      style={{ ...base, ...themed }}
    >
      {children}
    </button>
  );
}

// ─── Primitive: VoiceCtrl ────────────────────────────────────────────────────
// Handoff spec: 36×36, radius 9.
// primary     = gradient bg + white icon + shadow
// destructive = rgba(239,68,68,0.10) bg + rgba(239,68,68,0.30) border + #EF4444
// default     = var(--secondary) bg + 1px var(--border) + var(--foreground)

interface VoiceCtrlProps {
  children: React.ReactNode;
  primary?: boolean;
  destructive?: boolean;
  /** ANA-738 — amber "mic auto-locked" affordance, distinct from muted. */
  locked?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
}

function VoiceCtrl({
  children,
  primary,
  destructive,
  locked,
  onClick,
  disabled,
  title,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
}: VoiceCtrlProps) {
  const base: CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 9,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition: 'opacity 0.15s',
  };

  const themed: CSSProperties = primary
    ? {
        background: 'linear-gradient(135deg, #7C3AED, #3B82F6)',
        border: 'none',
        boxShadow: '0 4px 14px rgba(124,58,237,0.28)',
        color: 'white',
      }
    : destructive
      ? {
          background: 'rgba(239,68,68,0.10)',
          border: '1px solid rgba(239,68,68,0.30)',
          boxShadow: 'none',
          color: '#EF4444',
        }
      : locked
        ? {
            // ANA-738 — amber "working / mic paused" affordance, in the same wait
            // colour family as the thinking status, distinct from the muted look.
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.40)',
            boxShadow: 'none',
            color: '#F59E0B',
          }
        : {
            background: 'var(--secondary)',
            border: '1px solid var(--border)',
            boxShadow: 'none',
            color: 'var(--foreground)',
          };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? title}
      aria-pressed={ariaPressed}
      style={{ ...base, ...themed }}
    >
      {children}
    </button>
  );
}

/**
 * ANA-738 (B1 indicator) — the mic control as a THREE-state button: live (Mic),
 * user-muted (MicOff), or auto-locked during `thinking` (amber Mic + the
 * "tap to interrupt" accessible name). Deduped across the expanded + inline
 * chrome so both surfaces present the lock identically. The click is the one-tap
 * override in the locked state and the mute toggle otherwise — always the same
 * `onToggle` (the real LiveKit track toggle).
 */
function MicButton({ muted, micLocked, onToggle }: {
  muted: boolean;
  micLocked: boolean;
  onToggle: () => void;
}) {
  const p = micButtonPresentation(muted, micLocked);
  return (
    <VoiceCtrl
      onClick={onToggle}
      locked={p.mode === 'locked'}
      aria-label={p.ariaLabel}
      aria-pressed={p.ariaPressed}
      title={p.title}
    >
      {p.mode === 'muted'
        ? <MicOff style={{ width: 14, height: 14 }} />
        : <Mic style={{ width: 14, height: 14, opacity: p.mode === 'locked' ? 0.85 : 1 }} />}
    </VoiceCtrl>
  );
}

// ─── Primitive: status dots ──────────────────────────────────────────────────

function ListeningPulse() {
  return (
    <span
      data-testid="voice-status-dot"
      aria-hidden="true"
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#22C55E',
        boxShadow: '0 0 0 4px rgba(34,197,94,0.20)',
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  );
}

function ReadyDot({ accent }: { accent?: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: accent ?? 'var(--muted-foreground)',
        opacity: accent ? 1 : 0.55,
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  );
}

// ─── ModeToggleButton ────────────────────────────────────────────────────────
/**
 * Detailed ↔ Focused mode toggle, embedded inside the chat input bar.
 *
 * - Shows `Maximize2` when the shell is in `'detailed'` mode (clicking
 *   expands the thread to `'focused'`).
 * - Shows `Minimize2` when in `'focused'` mode (clicking collapses back
 *   to `'detailed'`).
 * - Renders nothing for `'peek'` (no input bar visible there) or when
 *   `onToggleMode` is missing.
 */
function ModeToggleButton({
  mode,
  onToggleMode,
}: {
  mode?: 'peek' | 'detailed' | 'focused';
  onToggleMode?: () => void;
}) {
  if (!mode || mode === 'peek' || !onToggleMode) return null;
  const isFocused = mode === 'focused';
  const Icon = isFocused ? Minimize2 : Maximize2;
  const label = isFocused ? 'Minimise to detailed' : 'Expand to focused';
  return (
    <ChatIconBtn
      data-testid="conversation-mode-toggle"
      data-mode={mode}
      onClick={onToggleMode}
      aria-label={label}
      aria-pressed={isFocused}
      title={label}
    >
      <Icon style={{ width: 14, height: 14 }} />
    </ChatIconBtn>
  );
}

// ─── TextZone ────────────────────────────────────────────────────────────────

function TextZone(props: {
  inputReady: boolean;
  textRequiresSessionReady: boolean;
  placeholder: string;
  onSend: (text: string) => Promise<void>;
  /** Turn in flight — disables Send + Enter (see ConversationInputBarProps.busy). */
  busy?: boolean;
  /** Mid-turn status shown in place of the field while busy (spec 015 US4 parity). */
  statusUpdate?: { text: string; label: string } | null;
  onSwitchToVoice: () => void;
  /** A3.2 — disable the Mic (voice-start) until the session id is committed. */
  voiceStartDisabled?: boolean;
  expanded: boolean;
  onToggleExpand?: () => void;
  expandLabel: string;
  ExpandIcon: typeof ChevronUp;
  mode?: 'peek' | 'detailed' | 'focused';
  onToggleMode?: () => void;
}) {
  const [value, setValue] = useState('');
  const trimmed = value.trim();
  const textGate = props.textRequiresSessionReady ? props.inputReady : true;
  // `!props.busy` makes both Send (disabled below) and Enter (submit() bails on
  // !canSend) inert while a turn is in flight — the obs-2 anti-double-send guard.
  const canSend = textGate && trimmed.length > 0 && !props.busy;
  // 017: voice-first primary state — when the field is empty the voice (soundwave)
  // button leads; once text is entered Send takes the primary slot.
  const hasText = trimmed.length > 0;
  const textDisabled = props.textRequiresSessionReady && !props.inputReady;
  // Text-mode status parity with voice: while a turn is in flight AND a
  // send_status_update has arrived, the input box shows the status in place of
  // the message field (the field is inert while busy anyway — obs-2 guard).
  // Before the first status lands, or once the turn completes, the field shows.
  const showStatus = !!(props.busy && props.statusUpdate);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const submit = () => {
    if (!canSend) return;
    void props.onSend(trimmed).then(() => setValue(''));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    submit();
  };

  return (
    <div
      data-testid="conversation-input-bar"
      aria-busy={props.textRequiresSessionReady && !props.inputReady}
      style={{
        ...chromeStyle(),
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px 10px 18px',
      }}
    >
      {showStatus ? (
        <div
          data-testid="text-input-status"
          role="status"
          aria-live="polite"
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <ListeningPulse />
          <span className="truncate text-sm text-muted-foreground">
            {props.statusUpdate!.text}
          </span>
        </div>
      ) : (
        <Input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={props.placeholder}
          aria-label="Message"
          style={{
            flex: 1,
            fontSize: 14,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            padding: 0,
            minWidth: 0,
          }}
          className="border-none bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          disabled={textDisabled}
        />
      )}

      <ModeToggleButton mode={props.mode} onToggleMode={props.onToggleMode} />

      <ChatIconBtn
        primary={!hasText}
        onClick={props.onSwitchToVoice}
        disabled={props.voiceStartDisabled}
        aria-label="Switch to voice"
        title={props.voiceStartDisabled ? 'Getting ready…' : 'Speak to Anaka'}
      >
        <AudioLines style={{ width: 14, height: 14 }} />
      </ChatIconBtn>

      <ChatIconBtn
        primary={hasText}
        onClick={() => submit()}
        disabled={!canSend}
        aria-label="Send"
        title="Send"
      >
        <Send style={{ width: 14, height: 14 }} />
      </ChatIconBtn>

      {props.onToggleExpand ? (
        <ChatIconBtn
          data-testid="expand"
          onClick={props.onToggleExpand}
          aria-label={props.expandLabel}
          aria-pressed={props.expanded}
          title={props.expandLabel}
        >
          <props.ExpandIcon style={{ width: 14, height: 14 }} />
        </ChatIconBtn>
      ) : null}
    </div>
  );
}

// ─── VoiceIdleZone ───────────────────────────────────────────────────────────

function VoiceIdleZone(props: {
  voiceState: VoiceState;
  voiceError: Error | null;
  /** ANA-575 — why the agent ended the call, when `voiceState === 'ended'`. */
  endedReason?: VoiceEndReason | null;
  onStartVoiceFromIdle: () => Promise<void>;
  /** A3.2 — block a FRESH voice start until the session id is committed (resume is exempt). */
  voiceStartDisabled?: boolean;
  // When paused, the primary control reads "Restart" and resumes the same
  // session (spec 015 — FR-VOICE-001/003) rather than starting fresh.
  paused?: boolean;
  pauseReason?: VoicePauseReason | null;
  onResume?: () => Promise<void>;
  onSwitchToText: () => void;
  expanded: boolean;
  expandLabel: string;
  ExpandIcon: typeof ChevronUp;
  onToggleExpand?: () => void;
  mode?: 'peek' | 'detailed' | 'focused';
  onToggleMode?: () => void;
}) {
  const [starting, setStarting] = useState(false);
  // 'preparing' = connected but Anaka hasn't spoken yet; keep the button in its
  // loading state until her greeting starts so it doesn't look ready early.
  const isConnecting =
    props.voiceState === 'authorizing' ||
    props.voiceState === 'connecting' ||
    props.voiceState === 'preparing' ||
    starting;

  // A3.2 — a FRESH start is blocked until the session id is committed; a paused
  // resume (Restart) already has a live session id, so it is never blocked.
  const startBlocked = !!props.voiceStartDisabled && !props.paused;

  const handlePrimary = async () => {
    if (isConnecting || startBlocked) return;
    setStarting(true);
    // Paused → resume the same session (Restart); otherwise start fresh.
    if (props.paused && props.onResume) {
      await props.onResume();
    } else {
      await props.onStartVoiceFromIdle();
    }
    setStarting(false);
  };

  const visual = mapVoiceVisualState(props.voiceState, undefined, undefined, props.endedReason);

  // When the call auto-paused on prolonged silence or dropped unexpectedly, say
  // so explicitly rather than the generic idle label — the user wants to know
  // WHY it stopped (and, for a drop, that tapping Restart picks back up rather
  // than starting over). A manual / max-session / tab-hide pause keeps the
  // neutral idle copy.
  const statusLabel =
    props.paused && props.pauseReason === 'idle'
      ? 'I paused the call due to no mic input.'
      : props.paused && props.pauseReason === 'dropped'
        ? 'The connection dropped — tap Restart to pick back up.'
        : visual.statusLabel;

  // Phased connect copy so the primary button matches the header label
  // (Starting session… / Connecting… / Getting ready…) instead of a generic
  // "Connecting…". `starting` precedes any voiceState change, so default to
  // "Connecting…" until the phased state lands.
  const connectingLabel =
    props.voiceState === 'authorizing'
      ? 'Starting session'
      : props.voiceState === 'preparing'
        ? 'Getting ready'
        : 'Connecting';
  // spec 015 — FR-VOICE-001: "Start" when idle/fresh, "Restart" when paused.
  // ANA-575 — after an intentional end, offer a FRESH start ("Start new
  // conversation"), never a Restart/resume of the call Anaka deliberately closed
  // (`paused` is false on an intentional end, so `handlePrimary` already starts
  // fresh rather than resuming).
  const idlePrimary = props.paused ? 'Restart' : 'Start';
  const primaryFor = (fallback: string) =>
    isConnecting
      ? connectingLabel
      : props.voiceState === 'error'
        ? 'Try again'
        : props.voiceState === 'ended'
          ? 'Start new conversation'
          : fallback;
  const primaryLabel = primaryFor(idlePrimary);
  const primaryAria = primaryFor(idlePrimary);

  // Voice bar size is decoupled from the page's focused mode: it's driven by
  // the `expanded` prop (a bar-local toggle) so we can show the expanded card
  // while the page stays in 'detailed' (dashboard visible).
  const isExpanded = !!props.expanded;

  if (isExpanded) {
    // ── EXPANDED layout (focused mode): column card ───────────────────────
    return (
      <div
        data-testid="voice-idle-zone"
        data-voice-phase={props.voiceState}
        style={{
          ...chromeStyle(),
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {/* header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReadyDot accent={visual.statusAccent} />
          <span
            // ANA-575 — announce the end-of-call status change to AT users.
            role="status"
            aria-live="polite"
            style={{
            fontSize: 12,
            fontWeight: 600,
            color: props.voiceState === 'error' ? '#EF4444' : 'var(--muted-foreground)',
            letterSpacing: '0.02em',
          }}>
            {props.voiceState === 'error'
              ? (props.voiceError?.message ?? 'Connection failed')
              : statusLabel}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{
            fontFamily: 'Fira Code, monospace',
            fontSize: 10,
            color: 'var(--muted-foreground)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}>Lobby</span>
          <ModeToggleButton mode={props.mode} onToggleMode={props.onToggleMode} />
          {props.onToggleExpand ? (
            <ChatIconBtn
              data-testid="expand"
              onClick={props.onToggleExpand}
              aria-label={props.expandLabel}
              aria-pressed={props.expanded}
              title={props.expandLabel}
            >
              <props.ExpandIcon style={{ width: 14, height: 14 }} />
            </ChatIconBtn>
          ) : null}
          <ChatIconBtn
            onClick={props.onSwitchToText}
            aria-label="Switch to text"
            title="Switch to text"
          >
            <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>✕</span>
          </ChatIconBtn>
        </div>

        {/* waveform row */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0', minHeight: 36 }}>
          <AnakaBarVisualizerView waveform="baseline" />
        </div>

        {/* controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => void handlePrimary()}
            disabled={isConnecting || startBlocked}
            aria-busy={isConnecting}
            aria-label={primaryAria}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px 8px 12px',
              borderRadius: 9999,
              border: 'none',
              background: isConnecting
                ? 'rgba(124,58,237,0.4)'
                : 'linear-gradient(135deg, #7C3AED, #3B82F6)',
              color: 'white',
              cursor: isConnecting ? 'not-allowed' : 'pointer',
              fontFamily: "'Open Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(124,58,237,0.32)',
              opacity: isConnecting ? 0.7 : 1,
            }}
          >
            <span style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.20)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Mic style={{ width: 12, height: 12 }} />
            </span>
            {primaryLabel}
          </button>
          <VoiceCtrl
            onClick={props.onSwitchToText}
            aria-label="Switch to text"
            title="Switch to text"
          >
            <Keyboard style={{ width: 14, height: 14 }} />
          </VoiceCtrl>
        </div>
      </div>
    );
  }

  // ── INLINE layout (default / mode === 'detailed') ─────────────────────────
  return (
    <div
      data-testid="voice-idle-zone"
      data-voice-phase={props.voiceState}
      style={{
        ...chromeStyle(),
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px 10px 16px',
      }}
    >
      <ReadyDot accent={visual.statusAccent} />

      <span
        // ANA-575 — announce the end-of-call status change to AT users, who are
        // exactly the voice-first users who may not be looking at the bar.
        role="status"
        aria-live="polite"
        style={{
        fontSize: 12,
        fontWeight: 600,
        color: props.voiceState === 'error' ? '#EF4444' : 'var(--muted-foreground)',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        // A long status string (e.g. the dropped-connection copy) must never
        // push the fixed-size controls out of the bar — `minWidth: 0` lets this
        // flex item shrink below its content size (the flexbox default is
        // `min-width: auto`, which is what caused the overflow) and the
        // overflow/ellipsis pair truncates once it does.
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {props.voiceState === 'error'
          ? (props.voiceError?.message ?? 'Connection failed')
          : statusLabel}
      </span>

      {/* dotted baseline waveform */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
        <AnakaBarVisualizerView waveform="baseline" dense />
      </div>

      <ChatIconBtn
        onClick={props.onSwitchToText}
        aria-label="Switch to text"
        title="Switch to text"
      >
        <Keyboard style={{ width: 14, height: 14 }} />
      </ChatIconBtn>

      <ModeToggleButton mode={props.mode} onToggleMode={props.onToggleMode} />

      {/* primary Start / Try again button */}
      <button
        type="button"
        onClick={() => void handlePrimary()}
        disabled={isConnecting || startBlocked}
        aria-busy={isConnecting}
        aria-label={primaryAria}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 32,
          borderRadius: 8,
          border: 'none',
          background: isConnecting
            ? 'rgba(124,58,237,0.4)'
            : 'linear-gradient(135deg, #7C3AED, #3B82F6)',
          boxShadow: '0 4px 14px rgba(124,58,237,0.30)',
          color: 'white',
          cursor: isConnecting ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          opacity: isConnecting ? 0.7 : 1,
          fontSize: 11,
          fontWeight: 600,
          gap: 4,
          whiteSpace: 'nowrap',
          padding: '0 10px',
        }}
      >
        <Mic style={{ width: 14, height: 14 }} />
        <span style={{ fontSize: 11 }}>
          {isConnecting ? '…' : props.voiceState === 'error' ? 'Retry' : idlePrimary}
        </span>
      </button>

      {props.onToggleExpand ? (
        <ChatIconBtn
          data-testid="expand"
          onClick={props.onToggleExpand}
          aria-label={props.expandLabel}
          aria-pressed={props.expanded}
          title={props.expandLabel}
        >
          <props.ExpandIcon style={{ width: 14, height: 14 }} />
        </ChatIconBtn>
      ) : null}
    </div>
  );
}

// ─── VoiceLiveChrome ─────────────────────────────────────────────────────────

interface VoiceLiveChromeProps {
  voiceState: VoiceState;
  statusLabelOverride?: string | null;
  /** ANA-407 — true while the two-phase idle "still there?" prompt is showing. */
  idlePrompting?: boolean;
  /** ANA-738 (B1) — true while the mic is auto-locked during `thinking`. */
  micLocked?: boolean;
  paused: boolean;
  voiceStartedAt: number | null;
  pausedMs: number;
  onPause: () => void;
  onResume: () => Promise<void>;
  onEndCall: () => void;
  onSwitchToText: () => void;
  expanded: boolean;
  expandLabel: string;
  ExpandIcon: typeof ChevronUp;
  onToggleExpand?: () => void;
  mode?: 'peek' | 'detailed' | 'focused';
  onToggleMode?: () => void;
  /** When false, render in-flow instead of the viewport-fixed overlay (017). */
  pinned?: boolean;
}

/** Must render under a LiveKit `RoomContext` — uses LiveKit microphone toggle (hardware mute). */
function VoiceLiveChrome({
  voiceState,
  statusLabelOverride,
  idlePrompting,
  micLocked,
  paused,
  voiceStartedAt,
  pausedMs,
  onPause,
  onResume,
  onEndCall,
  onSwitchToText,
  expanded,
  expandLabel,
  ExpandIcon,
  onToggleExpand,
  mode,
  onToggleMode,
  pinned = true,
}: VoiceLiveChromeProps) {
  const { toggle, enabled } = useTrackToggle({ source: Track.Source.Microphone });
  const muted = !enabled;

  const visual = mapVoiceVisualState(voiceState, statusLabelOverride, idlePrompting);

  // ANA-738 (B1 indicator) — an autonomous mic-state change needs a cue for
  // eyes-free / screen-reader users (the teal user-waveform goes flat, but they
  // can't see it). Play a short earcon on each lock engage/release (skip the very
  // first render so a fresh mount doesn't beep), and carry a polite `aria-live`
  // announcement (rendered below). Best-effort: audio failure never affects the call.
  const lockedBool = Boolean(micLocked);
  const prevLockedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevLockedRef.current === null) { prevLockedRef.current = lockedBool; return; }
    if (prevLockedRef.current !== lockedBool) {
      prevLockedRef.current = lockedBool;
      playMicLockEarcon(lockedBool);
    }
  }, [lockedBool]);
  const micLockLiveRegion = (
    <span
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
      }}
    >
      {micLockAnnouncement(lockedBool)}
    </span>
  );

  // Decoupled from page focused mode — driven by the bar-local `expanded` prop
  // (see VoiceIdleZone), so the expanded card shows while the page stays detailed.
  const isInline = !expanded;
  const wrapperStyle: CSSProperties = pinned ? FIXED_POSITION_STYLE : { width: '100%' };
  // Only replicate the content column's ~820px width when PINNED — that's the
  // one case where this chrome escapes ChatThread's own DOM flow (`position:
  // fixed; left:0; right:0`, full viewport width) and so has no other way to
  // line up with the page's content column. When not pinned (in-flow, e.g. the
  // scoping page), TextZone/VoiceIdleZone already just fill their parent with
  // no width cap of their own — applying one here on top of that made the
  // live-call bar narrower (and differently padded) than the text input bar
  // it sits alongside.
  const contentWrapStyle: CSSProperties | undefined = pinned
    ? { maxWidth: 820, margin: '0 auto', padding: '0 28px 22px' }
    : undefined;

  // ── EXPANDED card ─────────────────────────────────────────────────────────
  if (!isInline) {
    return (
      <div data-testid="voice-live-chrome" data-pinned={String(pinned)} style={{ ...wrapperStyle }}>
        <div style={contentWrapStyle}>
          <div
            style={{
              ...chromeStyle(),
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ListeningPulse />
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: visual.statusAccent,
                letterSpacing: '0.02em',
              }}>
                {visual.statusLabel}
              </span>
              {visual.timerVisible && (
                <VoiceSessionTimer
                  startedAt={voiceStartedAt}
                  paused={paused}
                  pausedMs={pausedMs}
                  className="font-mono text-[11px]"
                />
              )}
              <span style={{ flex: 1 }} />
              <span style={{
                fontFamily: 'Fira Code, monospace',
                fontSize: 10,
                color: 'var(--muted-foreground)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>Voice mode</span>
              <ModeToggleButton mode={mode} onToggleMode={onToggleMode} />
              {/* Minimise lives in the header next to the close button (matching
                  VoiceIdleZone) so its position is identical between the active
                  call and the paused/idle card — it was previously dropped in a
                  centered row below the controls during an active call. */}
              {onToggleExpand ? (
                <ChatIconBtn
                  data-testid="expand"
                  onClick={onToggleExpand}
                  aria-label={expandLabel}
                  aria-pressed={expanded}
                  title={expandLabel}
                >
                  <ExpandIcon style={{ width: 14, height: 14 }} />
                </ChatIconBtn>
              ) : null}
              <ChatIconBtn
                onClick={onSwitchToText}
                aria-label="Switch to text"
                title="Switch to text"
              >
                <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>✕</span>
              </ChatIconBtn>
            </div>

            {/* waveform row */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0', minHeight: 36 }}>
              <Suspense fallback={<AnakaBarVisualizerView waveform={visual.waveform} />}>
                <AnakaBarVisualizer />
              </Suspense>
            </div>

            {/* 4 VoiceCtrl controls */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              {micLockLiveRegion}
              <MicButton muted={muted} micLocked={lockedBool} onToggle={() => void toggle()} />

              <VoiceCtrl
                primary
                onClick={paused ? () => void onResume() : onPause}
                aria-label={paused ? 'Restart' : 'Pause'}
                aria-pressed={paused}
                title={paused ? 'Restart' : 'Pause'}
              >
                <Pause style={{ width: 14, height: 14 }} />
              </VoiceCtrl>

              <VoiceCtrl
                onClick={onSwitchToText}
                aria-label="Switch to text"
                title="Switch to text"
              >
                <Keyboard style={{ width: 14, height: 14 }} />
              </VoiceCtrl>

              <VoiceCtrl
                destructive
                onClick={onEndCall}
                aria-label="End voice session"
                title="End voice session"
              >
                <Square style={{ width: 14, height: 14 }} />
              </VoiceCtrl>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── INLINE (detailed or no mode) ──────────────────────────────────────────
  return (
    <div data-testid="voice-live-chrome" data-pinned={String(pinned)} style={{ ...wrapperStyle }}>
      <div style={contentWrapStyle}>
        <div
          style={{
            ...chromeStyle(),
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px 10px 16px',
          }}
        >
          {/* status dot + label/timer (timer stacks BELOW the label, not
              beside it — frees up horizontal room for the control row,
              which already has more fixed-size buttons than the idle bar). */}
          <ListeningPulse />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              color: visual.statusAccent,
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              // See VoiceIdleZone's matching status label — same overflow fix.
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {visual.statusLabel}
            </span>

            {visual.timerVisible && (
              <VoiceSessionTimer
                startedAt={voiceStartedAt}
                paused={paused}
                pausedMs={pausedMs}
                className="font-mono text-[11px]"
              />
            )}
          </div>

          {/* waveform */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
            <Suspense fallback={<AnakaBarVisualizerView waveform={visual.waveform} dense />}>
              <AnakaBarVisualizer />
            </Suspense>
          </div>

          {/* mute / mic-lock (ANA-738 tri-state) */}
          {micLockLiveRegion}
          {(() => {
            const p = micButtonPresentation(muted, lockedBool);
            return (
              <ChatIconBtn
                onClick={() => void toggle()}
                locked={p.mode === 'locked'}
                aria-label={p.ariaLabel}
                aria-pressed={p.ariaPressed}
                title={p.title}
              >
                {p.mode === 'muted'
                  ? <MicOff style={{ width: 14, height: 14 }} />
                  : <Mic style={{ width: 14, height: 14, opacity: p.mode === 'locked' ? 0.85 : 1 }} />}
              </ChatIconBtn>
            );
          })()}

          {/* keyboard → text */}
          <ChatIconBtn
            onClick={onSwitchToText}
            aria-label="Switch to text"
            title="Switch to text"
          >
            <Keyboard style={{ width: 14, height: 14 }} />
          </ChatIconBtn>

          {/* end call */}
          <button
            type="button"
            onClick={onEndCall}
            aria-label="End voice session"
            title="End voice session"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(239,68,68,0.10)',
              border: '1px solid rgba(239,68,68,0.30)',
              color: '#EF4444',
              cursor: 'pointer',
            }}
          >
            <PhoneOff style={{ width: 14, height: 14 }} />
          </button>

          <ModeToggleButton mode={mode} onToggleMode={onToggleMode} />

          {onToggleExpand ? (
            <ChatIconBtn
              data-testid="expand"
              onClick={onToggleExpand}
              aria-label={expandLabel}
              aria-pressed={expanded}
              title={expandLabel}
            >
              <ExpandIcon style={{ width: 14, height: 14 }} />
            </ChatIconBtn>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Root export ─────────────────────────────────────────────────────────────

export function ConversationInputBar({
  inputReady,
  textRequiresSessionReady = true,
  onSend,
  busy = false,
  placeholder = 'Type a message…',
  inputMode,
  onSwitchToVoice,
  onSwitchToText,
  onStartVoiceFromIdle,
  voiceStartDisabled = false,
  voiceState,
  statusLabelOverride,
  idlePrompting,
  sessionCapWarning,
  onExtendSession,
  onLockScope,
  textStatusUpdate,
  room,
  livekitUrl,
  micBlocked = false,
  micErrorKind,
  micLocked = false,
  paused,
  pauseReason,
  voiceError,
  voiceEndedReason,
  voiceStartedAt,
  pausedMs,
  onPause,
  onResume,
  onEndCall,
  expanded,
  onToggleExpand,
  mode,
  onToggleMode,
  pinnedVoiceChrome = true,
}: ConversationInputBarProps) {
  // When the caller doesn't dictate an explicit expanded state, default to the
  // compact/inline bar on mobile and the larger expanded card on tablet/desktop
  // — callers that own their own expand/collapse state (e.g. a manual toggle)
  // still pass an explicit boolean and take full control, unaffected by viewport.
  const isMobile = useIsMobile();
  const resolvedExpanded = expanded ?? !isMobile;
  const expandLabel = resolvedExpanded ? 'Collapse' : 'Expand';
  const ExpandIcon = resolvedExpanded ? ChevronDown : ChevronUp;

  if (inputMode === 'text') {
    return (
      <TextZone
        inputReady={inputReady}
        textRequiresSessionReady={textRequiresSessionReady}
        placeholder={placeholder}
        onSend={onSend}
        busy={busy}
        statusUpdate={textStatusUpdate}
        onSwitchToVoice={onSwitchToVoice}
        voiceStartDisabled={voiceStartDisabled}
        expanded={resolvedExpanded}
        onToggleExpand={onToggleExpand}
        expandLabel={expandLabel}
        ExpandIcon={ExpandIcon}
        mode={mode}
        onToggleMode={onToggleMode}
      />
    );
  }

  const hasRoomContext = Boolean(room && livekitUrl);
  // 'preparing' must keep the live chrome mounted — RoomAudioRenderer lives
  // inside it, so the greeting audio (which ends the preparing phase) can play.
  const chromeActiveStates: VoiceState[] = ['authorizing', 'connecting', 'preparing', 'listening', 'thinking', 'speaking', 'reconnecting'];
  const showLiveChrome = hasRoomContext && chromeActiveStates.includes(voiceState);

  if (showLiveChrome && room && livekitUrl) {
    return (
      <div data-testid="conversation-input-bar-voice-live">
        {micBlocked ? (
          <div
            role="status"
            data-testid="voice-mic-blocked-hint"
            className="text-muted-foreground mx-auto mb-1 block text-center text-xs"
          >
            {micErrorHint(micErrorKind)}
          </div>
        ) : null}
        {sessionCapWarning ? (
          <div
            role="status"
            data-testid="session-cap-warning-banner"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
              flexWrap: 'wrap',
              margin: '0 auto 6px',
              padding: '6px 10px',
              borderRadius: 8,
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#F59E0B',
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            <span>This call is about to wrap up.</span>
            <button
              type="button"
              onClick={onExtendSession}
              style={{
                border: 'none',
                background: 'none',
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Continue
            </button>
            <button
              type="button"
              onClick={onLockScope}
              style={{
                border: 'none',
                background: 'none',
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Lock the scope
            </button>
          </div>
        ) : null}
        {/*
          The room is connected imperatively by `useVoiceConversation`, so we
          only need to PROVIDE it to context-consuming children (mic toggle,
          visualizer) — not manage its connection. Do NOT use `<LiveKitRoom>`
          here: with `connect={false}` its mount effect calls `room.disconnect()`
          ("disconnecting because connect is false"), which aborts the in-flight
          `room.connect()` the hook is running and surfaces as the production
          "Client initiated disconnect" failure. `RoomContext.Provider` is
          context-only and has no connect/disconnect lifecycle.
        */}
        <RoomContext.Provider value={room}>
          {/* The agent audio sink (`<RoomAudioRenderer>`) is NOT rendered here —
              it lives in `<PersistentVoiceAudio>` (mounted by ConversationInner)
              so toggling the chat layout never remounts it and cuts the call.
              `StartAudio` renders a button ONLY when the browser blocks autoplay,
              letting the user tap to enable sound. */}
          <StartAudio
            label="Tap to enable sound"
            className="bg-secondary text-foreground border-border mx-auto block rounded-md border px-3 py-1 text-sm"
          />
          <VoiceLiveChrome
            voiceState={voiceState}
            statusLabelOverride={statusLabelOverride}
            idlePrompting={idlePrompting}
            micLocked={micLocked}
            paused={paused}
            voiceStartedAt={voiceStartedAt}
            pausedMs={pausedMs}
            onPause={onPause}
            onResume={onResume}
            onEndCall={onEndCall}
            onSwitchToText={onSwitchToText}
            expanded={resolvedExpanded}
            expandLabel={expandLabel}
            ExpandIcon={ExpandIcon}
            onToggleExpand={onToggleExpand}
            mode={mode}
            onToggleMode={onToggleMode}
            pinned={pinnedVoiceChrome}
          />
        </RoomContext.Provider>
      </div>
    );
  }

  return (
    <VoiceIdleZone
      voiceState={voiceState}
      voiceError={voiceError}
      endedReason={voiceEndedReason}
      onStartVoiceFromIdle={onStartVoiceFromIdle}
      voiceStartDisabled={voiceStartDisabled}
      paused={paused}
      pauseReason={pauseReason}
      onResume={onResume}
      onSwitchToText={onSwitchToText}
      expanded={resolvedExpanded}
      expandLabel={expandLabel}
      ExpandIcon={ExpandIcon}
      onToggleExpand={onToggleExpand}
      mode={mode}
      onToggleMode={onToggleMode}
    />
  );
}
